import type { VWOExperiments } from '@/types/ThirdPartyIntegrations/VWO'
import { useEffect, useState } from 'react'
import { error } from '@/services/Log'
import { nonNull } from './nullish'

// Documentation on how to implement VWO experiments is available on the file:
// types/ThirdPartyIntegrations/VWO.d.ts
export const getVWOExperiment = (experimentKey: keyof VWOExperiments) => {
  if (typeof window === undefined) {
    return Promise.reject(new Error('SSR VWO Experiment is not supported'))
  }

  // VWO runs a code snippet if the variant is turned on
  // which calls 'VWOExperimentResolvers[experimentKey]' method if it exists
  // or 'VWOExperiments[experimentKey] = Promise.resolve(experimentValue)'
  // More details on types/ThirdPartyIntegrations/VWO.d.ts file
  if (typeof window?.VWOExperiments?.[experimentKey] !== 'object') {
    const vwoExperiment = new Promise((resolve) => {
      window.VWOExperimentResolvers = {
        ...(window.VWOExperimentResolvers || {}),
        // This 'resolve' will be called by VWO script with the experimentValue
        [experimentKey]: (val: boolean) => {
          resolve(val)
        },
      }

      setTimeout(() => {
        error(new Error(`VWO Experiment: ${experimentKey} timeout`))
        resolve(false)
      }, 5000) // 5 seconds
    })
    window.VWOExperiments = {
      [experimentKey]: vwoExperiment,
      ...(window.VWOExperiments || {}),
    }
  }
  return (
    window.VWOExperiments[experimentKey]
    || Promise.reject(new Error('VWO Experiment not found'))
  )
}

export const useGetExperiment = (experimentKey: keyof VWOExperiments, mockedValue?: boolean) => {
  const [isExperimentOn, setIsExperimentOn] = useState(false)
  useEffect(() => {
    if (nonNull(mockedValue)) {
      setIsExperimentOn(mockedValue)
    } else {
      getVWOExperiment(experimentKey)
        .then((value) => {
          setIsExperimentOn(value)
        })
        .catch((e) => {
          console.error('useGetExperiment Error', e)
        })
    }
  }, [experimentKey, mockedValue])
  return isExperimentOn
}

export default getVWOExperiment
